import { useEffect, useState } from "react";
import { ApiError } from "../lib/api/client";
import { listMembers } from "../lib/api/endpoints";
import { MEMBER_ENROLLMENT_TYPE, memberEnrollmentTypeLabel } from "../lib/enums";
import { useSession } from "../lib/session/SessionContext";
import { C } from "../theme";
import type { MemberDto } from "../types";
import { Button } from "./Button";
import { Card } from "./Card";
import { FieldInput } from "./Field";
import { FilterPills } from "./FilterPills";
import { Label } from "./Label";

const PAGE_SIZE = 20;
const FILTERS = ["Todos", "Wellhub", "TotalPass"];

function enrollmentTypes(filter: string): number[] {
  if (filter === "Wellhub") return [MEMBER_ENROLLMENT_TYPE.Wellhub];
  if (filter === "TotalPass") return [MEMBER_ENROLLMENT_TYPE.Totalpass];
  return [MEMBER_ENROLLMENT_TYPE.Wellhub, MEMBER_ENROLLMENT_TYPE.Totalpass];
}

function errorMessage(error: unknown): string {
  return error instanceof ApiError ? error.detail ?? error.message : "Não foi possível carregar os alunos.";
}

interface MembershipMembersTabProps {
  onConfirmCheckIn?: (member: MemberDto) => void;
  checkingInMemberId?: string | null;
  checkedInMemberIds?: readonly string[];
}

export function MembershipMembersTab({ onConfirmCheckIn, checkingInMemberId, checkedInMemberIds = [] }: MembershipMembersTabProps) {
  const { accessToken } = useSession();
  const token = accessToken ?? "";
  const [members, setMembers] = useState<MemberDto[] | null>(null);
  const [filter, setFilter] = useState("Todos");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setMembers(null);
    setLoadError(null);
    listMembers(token, { page, pageSize: PAGE_SIZE, search, enrollmentTypes: enrollmentTypes(filter) })
      .then(result => {
        if (cancelled) return;
        setMembers(result.items);
        setTotalCount(result.totalCount);
      })
      .catch(error => {
        if (cancelled) return;
        setMembers([]);
        setLoadError(errorMessage(error));
      });
    return () => { cancelled = true; };
  }, [token, filter, page, search, reloadKey]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const isCheckedIn = (memberId: string) => checkedInMemberIds.includes(memberId);

  return (
    <Card style={{ marginTop: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <Label>Alunos Wellhub e TotalPass</Label>
          <div style={{ color: C.light, fontSize: 12, marginTop: 3 }}>Consulte os vínculos ativos para a operação do dia.</div>
        </div>
        <span style={{ color: C.gray, fontSize: 12, fontWeight: 700 }}>{totalCount} {totalCount === 1 ? "aluno" : "alunos"}</span>
      </div>

      <div style={{ display: "flex", gap: 12, marginTop: 14, alignItems: "center", flexWrap: "wrap" }}>
        <FilterPills options={FILTERS} value={filter} onChange={value => { setFilter(value); setPage(1); }} />
        <FieldInput aria-label="Buscar aluno Wellhub ou TotalPass" type="search" placeholder="Buscar por nome ou matrícula" value={searchInput} onChange={event => setSearchInput(event.target.value)} style={{ flex: 1, minWidth: 220 }} />
      </div>

      {loadError && <div role="alert" style={{ color: C.red, background: C.redSoft, borderRadius: 10, padding: 12, marginTop: 14, fontSize: 12.5 }}>{loadError} <Button variant="ghost" onClick={() => setReloadKey(value => value + 1)}>Tentar novamente</Button></div>}
      {members === null && !loadError && <div style={{ color: C.light, padding: "22px 0", fontSize: 13 }}>Carregando alunos...</div>}
      {members?.length === 0 && !loadError && <div style={{ color: C.light, padding: "22px 0", fontSize: 13 }}>{search ? "Nenhum aluno encontrado para esta busca." : "Nenhum aluno Wellhub ou TotalPass encontrado."}</div>}

      {members && members.length > 0 && <div style={{ overflowX: "auto", marginTop: 14 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760, fontSize: 12.5 }}>
          <thead><tr style={{ color: C.light, textAlign: "left" }}><th style={{ padding: "8px 10px", borderBottom: `1px solid ${C.faint}` }}>Aluno</th><th style={{ padding: "8px 10px", borderBottom: `1px solid ${C.faint}` }}>Matrícula</th><th style={{ padding: "8px 10px", borderBottom: `1px solid ${C.faint}` }}>Plano</th><th style={{ padding: "8px 10px", borderBottom: `1px solid ${C.faint}` }}>Origem</th><th style={{ padding: "8px 10px", borderBottom: `1px solid ${C.faint}` }}>Turma</th>{onConfirmCheckIn && <th aria-label="Ações" style={{ padding: "8px 10px", borderBottom: `1px solid ${C.faint}` }} />}</tr></thead>
          <tbody>{members.map(member => <tr key={member.id} style={{ borderBottom: `1px solid ${C.faint}` }}><td style={{ padding: "11px 10px", fontWeight: 700, color: C.ink }}>{member.name}</td><td style={{ padding: "11px 10px", color: C.gray }}>{member.registrationNumber}</td><td style={{ padding: "11px 10px", color: C.gray }}>{member.enrollment?.planName ?? "—"}</td><td style={{ padding: "11px 10px", color: C.ink }}>{member.enrollment ? memberEnrollmentTypeLabel(member.enrollment.type) : "—"}</td><td style={{ padding: "11px 10px", color: C.light }}>—</td>{onConfirmCheckIn && <td style={{ padding: "8px 10px", textAlign: "right" }}>{!isCheckedIn(member.id) && <Button onClick={() => onConfirmCheckIn(member)} disabled={checkingInMemberId !== null && checkingInMemberId !== undefined}>{checkingInMemberId === member.id ? "Validando..." : "Confirmar check-in"}</Button>}</td>}</tr>)}</tbody>
        </table>
      </div>}

      {totalCount > PAGE_SIZE && <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14 }}><span style={{ color: C.light, fontSize: 12 }}>Página {page} de {totalPages}</span><div style={{ display: "flex", gap: 8 }}><Button variant="outline" disabled={page === 1} onClick={() => setPage(value => value - 1)}>Anterior</Button><Button variant="outline" disabled={page >= totalPages} onClick={() => setPage(value => value + 1)}>Próxima</Button></div></div>}
    </Card>
  );
}
